import { api } from '../../../utility/apiClient';
import { DEFAULT_PLAN_TIER } from '../../../constants/plans';

export const authService = {
  /** POST /api/v1/auth/login → { token, user } */
  async login(email, password) {
    return api.post('/api/v1/auth/login', { email, password })
      .catch((err) => { throw new Error(err.message || 'Login failed. Please check your credentials.'); });
  },

  /** POST /api/v1/auth/forgot-password */
  async forgotPassword(email) {
    return api.post('/api/v1/auth/forgot-password', { email })
      .catch((err) => { throw new Error(err.message || 'Failed to send reset email. Please try again.'); });
  },

  /** PUT /api/v1/auth/change-password */
  async changePassword(currentPassword, newPassword) {
    return api.put('/api/v1/auth/change-password', { currentPassword, newPassword })
      .catch((err) => { throw new Error(err.message || 'Failed to change password. Please try again.'); });
  },

  /** GET /api/v1/auth/profile — returns current user with up-to-date plan_tier */
  async getProfile() {
    return api.get('/api/v1/auth/profile')
      .catch((err) => { throw new Error(err.message || 'Failed to fetch profile.'); });
  },

  /** POST /api/v1/auth/refresh-token — cookie-based refresh */
  async refreshSession() {
    return api.post('/api/v1/auth/refresh-token', {})
      .catch((err) => { throw new Error(err.message || 'Failed to refresh session.'); });
  },

  /** PUT /api/v1/auth/profile */
  async updateProfile({ name, company_name, job_role, bio }) {
    return api.put('/api/v1/auth/profile', { name, company_name, job_role, bio })
      .catch((err) => { throw new Error(err.message || 'Failed to update profile. Please try again.'); });
  },

  /** POST /api/v1/auth/register → { token, user }
   *  Payload shape expected by the API:
   *  { name, email, password, company_name, plan_tier, role }
   */
  async register({ fullName, contactName, email, password, companyName, plan, employees, countryCode, phone, jobTitle }) {
    // Enterprise has no password field — generate a secure random one so the API doesn't reject it
    const effectivePassword =
      password || (crypto.randomUUID().slice(0, 12) + 'A1!');

    const payload = {
      name:         fullName || contactName || '',
      email,
      password:     effectivePassword,
      company_name: companyName  || '',
      plan_tier:    plan ? (plan.charAt(0).toUpperCase() + plan.slice(1)) : DEFAULT_PLAN_TIER,
      role:         'user',
    };

    // Enterprise-specific fields
    if (employees) {
      // employees is a range string like "1 – 50" — extract the first number
      const match = employees.match(/\d+/);
      if (match) payload.no_of_employees = Number(match[0]);
    }
    if (phone) {
      payload.phone = `${countryCode || '+91'}${phone.replace(/\D/g, '')}`;
    }
    // Professional / Business extras
    if (jobTitle)   payload.job_role = jobTitle;

    return api.post('/api/v1/auth/register', payload)
      .catch((err) => { throw new Error(err.message || 'Registration failed. Please try again.'); });
  },

  /** POST /api/v1/auth/logout — clears httpOnly cookies on backend */
  async logout() {
    try {
      await api.post('/api/v1/auth/logout', {});
    } catch {
      // Ignore errors — proceed with local cleanup even if backend call fails
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_user');
  },

  getStoredAuth() {
    try {
      const token = localStorage.getItem('auth_token');
      const raw   = localStorage.getItem('auth_user');
      return { token, user: raw ? JSON.parse(raw) : null };
    } catch {
      return { token: null, user: null };
    }
  },

  storeAuth(token, refreshToken, user) {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_refresh_token', refreshToken);
    localStorage.setItem('auth_user', JSON.stringify(user));
  },
};
